import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import _ from 'lodash';
import {clientApi} from '@/http/ClientApi';
import {store} from '@returns-centre/store';

@Module({name: 'customerModule', namespaced: true})
export default class CustomerModule extends VuexModule {
    orders: Order[] = [];
    trade_ins_resource: TradeInsResource | null = null;
    trade_ins: TradeIn[] = [];
    trade_in: TradeIn | null = null;
    verified_order: Order | null = null;
    returns_portal_shopify_customer: (ReturnsPortalShopifyCustomer | null) = null;
    errors: ValidationErrors = {};
    is_verifying_order = false;
    is_fetching_orders = false;
    is_fetching_trade_ins = false;
    is_fetching_trade_in = false;
    verified_order_session_key = 'verified_order';

    get returnsPortalShopifyCustomer(): (ReturnsPortalShopifyCustomer | null) {
        return this.returns_portal_shopify_customer;
    }

    get getOrders(): (Order[] | null) {
        return this.orders;
    }

    get tradeInsResource(): (TradeInsResource | null) {
        return this.trade_ins_resource;
    }

    get getTradeIns(): (TradeIn[] | null) {
        return this.trade_ins;
    }

    get hasOrders(): boolean {
        return this.orders.length > 0;
    }

    get hasTradeIns() {
        return !_.isEmpty(this.trade_ins);
    }

    get hasTradeIn() {
        return !_.isEmpty(this.trade_in);
    }

    get getErrors(): ValidationErrors {
        return this.errors;
    }

    get hasErrors() {
        return !_.isEmpty(this.errors);
    }

    get getError() {
        return (key: string) => _.hasIn(this.errors, key) ? this.errors[key] : null;
    }

    get isVerifyingOrder(): boolean {
        return this.is_verifying_order;
    }

    get isFetchingOrders(): boolean {
        return this.is_fetching_orders;
    }

    get isFetchingTradeIns(): boolean {
        return this.is_fetching_trade_ins;
    }

    get verifiedOrder(): Order | null {
        return this.verified_order;
    }

    get hasVerifiedOrder() {
        return !!this.verified_order;
    }

    get hasLoggedInUser() {
        return !!this.returns_portal_shopify_customer && !!this.returns_portal_shopify_customer.id;
    }

    get loggedInUser(): ReturnsPortalShopifyCustomer | null {
        if (this.returns_portal_shopify_customer && this.returns_portal_shopify_customer.id) {
            return this.returns_portal_shopify_customer;
        }
        return null;
    }

    @Action({commit: 'SET_RETURNS_PORTAL_SHOPIFY_CUSTOMER'})
    setReturnsPortalShopifyCustomer(customer: ReturnsPortalShopifyCustomer) {
        return customer;
    }

    @Action({commit: 'RESET_ORDERS'})
    resetOrders(): void {
    }

    @Action({commit: 'RESET_TRADE_INS'})
    resetTradeIns(): void {
    }

    @Action({commit: 'SET_ORDERS'})
    setOrders(orders: Order[] = []) {
        return orders;
    }

    @Action({commit: 'SET_ORDER'})
    setOrder(order: Order): Order {
        return order;
    }

    @Action({commit: 'SET_TRADEIN'})
    setTradeIn(tradeIn: TradeIn): TradeIn {
        return tradeIn;
    }

    @Action({commit: 'SET_TRADE_INS'})
    setTradeIns(tradeIns: TradeIn[] = []) {
        return tradeIns;
    }

    @Action
    async fetchOrders(customer: ReturnsPortalShopifyCustomer | null) {
        this.context.commit('SET_ORDERS', []);
        if (!customer) {
            return;
        }
        this.context.commit('SET_CONFIRMED_TRADE_IN', null);
        this.context.commit('SET_IS_FETCHING_ORDERS', true);
        try {
            const res = await clientApi.getCustomerOrders(customer);
            this.context.commit('SET_ORDERS', res.data.orders);
            this.context.commit('SET_IS_FETCHING_ORDERS', false);
        } catch (e) {
            this.context.commit('SET_ERRORS', e.response.data.errors);
            this.context.commit('SET_IS_FETCHING_ORDERS', false);
        }
    }

    @Action
    async fetchTradeIns<T extends object>(params: T): Promise<void> {
        if (!this.returns_portal_shopify_customer) {
            return;
        }
        this.context.commit('SET_IS_FETCHING_TRADE_INS', true);
        await clientApi.getCustomerTradeIns({
            ...params,
            shopify_customer_id: this.returns_portal_shopify_customer.id
        }).then((res) => {
            this.context.commit('SET_TRADE_INS', res);
        }).catch((e) => {
            this.context.commit('SET_ERRORS', e.response.data.errors);
        }).finally(() => {
            this.context.commit('SET_IS_FETCHING_TRADE_INS', false);
        });
    }

    @Action
    async fetchTradeIn(id: number): Promise<void> {
        if (!this.returns_portal_shopify_customer) {
            return;
        }

        this.context.commit('SET_IS_FETCHING_TRADE_IN', true);

        try {
            const response = await clientApi.getTradeIn(id, {
                shopify_customer_id: this.returns_portal_shopify_customer.id
            });
            this.context.commit('SET_TRADE_IN', response);
        } catch (e) {
            this.context.commit('SET_ERRORS', e.response.data.errors);
        } finally {
            this.context.commit('SET_IS_FETCHING_TRADE_IN', false);
        }
    }

    @Action
    async verifyOrder(data: OrderToVerify) {
        this.context.commit('RESET_ERRORS');
        this.context.commit('SET_IS_VERIFYING_ORDER', true);

        try {
            const res = await clientApi.verifyOrder({
                ...data,
                shop_domain: this.context.rootState.shopModule.shop.shop_domain
            } as VerifyOrderRequest);
            this.context.commit('SET_RETURNS_PORTAL_SHOPIFY_CUSTOMER', res.data.customer);
            this.context.commit('SET_ORDERS', res.data.orders);
            this.context.commit('SET_VERIFIED_ORDER', res.data.orders[0]);
            return true;
        } catch (e) {
            this.context.commit('SET_ORDERS', []);
            this.context.commit('SET_ERRORS', e.response.data.errors);
            return false;
        } finally {
            this.context.commit('SET_IS_VERIFYING_ORDER', false);
        }
    }

    @Mutation
    SET_RETURNS_PORTAL_SHOPIFY_CUSTOMER(customer: ReturnsPortalShopifyCustomer) {
        this.returns_portal_shopify_customer = customer;
    }

    @Mutation
    SET_TRADE_INS(tradeInsResource: TradeInsResource | null): void {
        if (!tradeInsResource) {
            return;
        }
        this.trade_ins_resource = tradeInsResource;
        this.trade_ins = tradeInsResource.data;
    }

    @Mutation
    SET_TRADE_IN(tradeIn: TradeIn): void {
        this.trade_in = tradeIn;
        // this id done just to reuse the trade-in-items-view component in the customer trade in details view page
        // same component is also being used during trade in process and in trade-in confirmed page.
        store.state.tradeInModule.confirmed_trade_in = tradeIn;
    }

    @Mutation
    SET_CONFIRMED_TRADE_IN(tradeIn: TradeIn | null = null): void {
        store.state.tradeInModule.confirmed_trade_in = tradeIn;
    }


    @Mutation
    SET_ORDERS(orders: Order[]): void {
        this.orders = orders;
    }

    @Mutation
    SET_ORDER(order: Order): void {
        const orderIndex = _.findIndex(this.orders, {id: order.id});
        if (orderIndex < 0) {
            return;
        }
        this.orders[orderIndex] = order;
    }

    @Mutation
    SET_ERRORS(errors: ValidationErrors): void {
        this.errors = errors;
    }

    @Mutation
    RESET_ERRORS(): void {
        this.errors = {};
    }

    @Mutation
    SET_IS_VERIFYING_ORDER(val: boolean): void {
        this.is_verifying_order = val;
    }

    @Mutation
    SET_IS_FETCHING_ORDERS(val: boolean): void {
        this.is_fetching_orders = val;
    }

    @Mutation
    RESET_ORDERS(): void {
        this.orders = [];
    }

    @Mutation
    RESET_TRADE_INS(): void {
        this.trade_ins = [];
        this.trade_in = null;
    }

    @Mutation
    SET_IS_FETCHING_TRADE_INS(val: boolean): void {
        this.is_fetching_trade_ins = val;
    }

    @Mutation
    SET_IS_FETCHING_TRADE_IN(val: boolean): void {
        this.is_fetching_trade_in = val;
    }

    @Mutation
    SET_VERIFIED_ORDER(order: Order): void {
        this.verified_order = order;
    }
}
